/**
 * GaplessScheduler (BOU-1809)
 *
 * The narration queue historically played each frame-split MP3 segment through a
 * single reused HTMLAudioElement (set src → load → play → await `ended` → next).
 * Swapping the element's source between segments costs an element-load + decode +
 * event-loop hop, so a gap was audible at every frame boundary (the stutter the
 * code itself instrumented as "inter-segment gap"). Because narration cards are
 * `frame_split=true` into 4–7 small segments, those gaps compounded.
 *
 * This scheduler removes the gap: it decodes each segment into an AudioBuffer and
 * schedules them back-to-back on ONE AudioContext timeline. Each segment starts
 * at the exact context time the previous one ends (`nextStartTime`), so playback
 * is sample-accurate and gapless. Volume/mute run through a shared GainNode.
 *
 * The scheduler is intentionally dumb: no queue/dedup/ack/suppression logic (that
 * stays in useUserAudioQueue). It exposes decode+schedule, stop, and gain control,
 * plus per-segment onStart/onEnd callbacks so the caller can keep its
 * reveal-on-audio-start, gap instrumentation, and lookahead behavior unchanged.
 *
 * Any decode/context failure throws so the caller can fall back to the legacy
 * HTMLAudioElement path for that segment — worst case, behavior is unchanged.
 */

// Small cushion when the timeline has drained (queue underran): start the next
// segment a hair ahead of the live playhead rather than exactly at it, so the
// scheduled start is not already in the past by the time start() runs.
export const SCHEDULE_CUSHION_SEC = 0.06;

export class GaplessScheduler {
  constructor() {
    /** @type {AudioContext|null} */
    this.ctx = null;
    /** @type {GainNode|null} */
    this.gain = null;
    /** Context-time cursor: when the next scheduled segment should begin. */
    this.nextStartTime = 0;
    /** @type {Set<AudioBufferSourceNode>} */
    this.activeSources = new Set();
    /** Pending whenDrained() waiters, so stop() can resolve them immediately. */
    this._drainWaiters = new Set();
    this._handleContextStateChange = () => {
      if (this.ctx?.state === 'running') this._reconcileTimeline();
    };
    this.volume = 1;
    this.muted = false;
  }

  /** Lazily create the AudioContext + GainNode. Throws if Web Audio is absent. */
  ensureContext() {
    if (this.ctx) return this.ctx;
    const AC = (typeof window !== 'undefined')
      && (window.AudioContext || window.webkitAudioContext);
    if (!AC) throw new Error('WebAudioUnsupported');
    this.ctx = new AC();
    this.ctx.addEventListener?.('statechange', this._handleContextStateChange);
    this.gain = this.ctx.createGain();
    this.gain.gain.value = this.muted ? 0 : this.volume;
    this.gain.connect(this.ctx.destination);
    return this.ctx;
  }

  /** Resume a suspended or interrupted context. Safe to call repeatedly. */
  async resume() {
    this.ensureContext();
    if (this.ctx.state === 'suspended' || this.ctx.state === 'interrupted') {
      try { await this.ctx.resume(); } catch { /* best-effort */ }
    }
    this._reconcileTimeline();
  }

  get isRunning() {
    return Boolean(this.ctx && this.ctx.state === 'running');
  }

  setVolume(v) {
    this.volume = Number.isFinite(v) ? v : 1;
    if (this.gain && !this.muted) this.gain.gain.value = this.volume;
  }

  setMuted(m) {
    this.muted = Boolean(m);
    if (this.gain) this.gain.gain.value = this.muted ? 0 : this.volume;
  }

  /**
   * Decode `arrayBuffer` and schedule it to play at the current gapless cursor.
   * Resolves once scheduled (NOT when playback ends) so the caller can decode +
   * schedule the following segment while this one plays — that lookahead is what
   * keeps playback gapless.
   *
   * @param {ArrayBuffer} arrayBuffer - raw MP3 bytes for one segment.
   * @param {object}   [cb]
   * @param {Function} [cb.onStart] - fired (approximately) when the segment
   *   becomes audible. Web Audio has no 'playing' event, but the start time is
   *   known exactly, so a timer at that time is an accurate stand-in.
   * @param {Function} [cb.onEnd]   - fired when the segment finishes (source
   *   `ended`), or when it is stopped early.
   * @param {Function} [cb.shouldSchedule] - optional guard checked after async
   *   decode and before creating/starting a source. Return false to abort.
   * @param {string} [cb.playbackGroup] - group metadata used for selective
   *   pending-source cancellation.
   * @returns {Promise<{startTime:number, endTime:number, duration:number}>}
   */
  async schedule(arrayBuffer, {
    onStart, onEnd, shouldSchedule, onAbort, playbackGroup,
  } = {}) {
    const ctx = this.ensureContext();
    // decodeAudioData detaches the buffer; callers must not reuse it.
    const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

    // A skip/suppression during decodeAudioData cannot stop this segment yet
    // because no source has been added to activeSources. Re-check before start.
    if (shouldSchedule && !shouldSchedule()) {
      const err = new Error('Playback aborted');
      err.name = 'AbortError';
      throw err;
    }

    const now = ctx.currentTime;
    if (this.nextStartTime <= now) {
      // Timeline drained (first segment or an underrun) — start just ahead of
      // the live playhead so start() is never scheduled in the past. When the
      // cursor is still ahead of the playhead (mid-stream), keep it: that is
      // what makes consecutive segments play gaplessly.
      this.nextStartTime = now + SCHEDULE_CUSHION_SEC;
    }
    const startAt = this.nextStartTime;
    const endAt = startAt + audioBuffer.duration;

    const source = ctx.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(this.gain);
    this.activeSources.add(source);

    // [Codex] onAbort lets stop() notify a waiter still pending its onStart (e.g.
    // stopped during the cushion before the segment plays) so the caller can
    // reject instead of hanging forever. __started gates that: once the segment
    // has started, stop() relies on onended not firing (cleared below) rather
    // than onAbort.
    source.__onAbort = onAbort;
    source.__started = false;
    source.__startAt = startAt;
    source.__endTime = endAt;
    source.__playbackGroup = playbackGroup ?? null;
    let startFired = false;
    source.__fireStart = () => {
      if (startFired) return;
      startFired = true;
      source.__started = true;
      onStart?.();
    };
    source.__startTimer = null;

    source.onended = () => {
      // A natural end proves the source became audible even if a throttled
      // wall timer did not observe the audio clock crossing its start time.
      source.__fireStart?.();
      if (source.__startTimer !== null) clearTimeout(source.__startTimer);
      source.__startTimer = null;
      this.activeSources.delete(source);
      source.__fireStart = null;
      onEnd?.();
    };

    source.start(startAt);
    this._armSourceStart(source);
    this.nextStartTime = endAt;
    return { startTime: startAt, endTime: endAt, duration: audioBuffer.duration };
  }

  _armSourceStart(source) {
    if (source.__startTimer !== null) clearTimeout(source.__startTimer);
    source.__startTimer = null;
    if (
      !this.activeSources.has(source)
      || source.__started
      || this.ctx?.state !== 'running'
    ) return;

    const remainingMs = Math.max(
      0,
      (source.__startAt - this.ctx.currentTime) * 1000,
    );
    if (remainingMs <= 0) {
      source.__fireStart?.();
      return;
    }
    source.__startTimer = setTimeout(() => {
      source.__startTimer = null;
      this._armSourceStart(source);
    }, remainingMs);
  }

  insertGap(gapMs) {
    const gapSec = Number(gapMs) / 1000;
    if (!Number.isFinite(gapSec) || gapSec <= 0) return;

    const now = this.ctx ? this.ctx.currentTime : 0;
    if (this.nextStartTime <= now) {
      this.nextStartTime = now + gapSec;
      return;
    }
    this.nextStartTime += gapSec;
  }

  /** Cancel pending sources except continuations in the preserved group. */
  cancelPending({ preservePlaybackGroup = null } = {}) {
    for (const source of [...this.activeSources]) {
      if (
        source.__started
        || (
          preservePlaybackGroup
          && source.__playbackGroup === preservePlaybackGroup
        )
      ) continue;
      source.onended = null;
      if (source.__startTimer !== null) {
        clearTimeout(source.__startTimer);
        source.__startTimer = null;
      }
      source.__fireStart = null;
      try { source.stop(); } catch { /* already stopped */ }
      this.activeSources.delete(source);
      if (source.__onAbort) {
        try { source.__onAbort(); } catch { /* ignore */ }
      }
    }

    const now = this.ctx ? this.ctx.currentTime : 0;
    const activeEndTimes = [...this.activeSources]
      .map((source) => source.__endTime)
      .filter(Number.isFinite);
    this.nextStartTime = Math.max(now, ...activeEndTimes);
    for (const entry of [...this._drainWaiters]) {
      this._armDrainWaiter(entry);
    }
  }

  /** Stop every scheduled/playing segment and reset the cursor. */
  stop() {
    for (const source of this.activeSources) {
      try {
        source.onended = null;
        // A stopped future source should not reveal a card via its onStart timer.
        if (source.__startTimer !== null) {
          clearTimeout(source.__startTimer);
          source.__startTimer = null;
        }
        source.__fireStart = null;
        source.stop();
      } catch { /* already stopped */ }
      // [Codex] If the segment never started (stopped during the cushion), its
      // onended won't fire — notify the pending playChunkGapless waiter to abort
      // so playQueueChunks reaches its AbortError recovery (releases the queue id)
      // instead of hanging with the chunk deduped-but-unplayed.
      if (!source.__started && source.__onAbort) {
        try { source.__onAbort(); } catch { /* ignore */ }
      }
    }
    this.activeSources.clear();
    this.nextStartTime = this.ctx ? this.ctx.currentTime : 0;
    // [Codex] Resolve any in-flight whenDrained() waiters NOW. Their fixed timer
    // was sized to the old audio duration; without this a stop() mid-drain leaves
    // the caller awaiting the full remaining time, during which a new processQueue
    // run can start and the stale wait's finally could clear the new run's state.
    for (const entry of this._drainWaiters) {
      clearTimeout(entry.timer);
      entry.timer = null;
      entry.resolve();
    }
    this._drainWaiters.clear();
  }

  /**
   * Resolve once the playhead reaches the scheduler cursor, including any
   * intentional gap reserved after the last active source, OR when stop() is
   * called. The caller awaits this before dropping its "playing" state because
   * playChunkGapless resolves at segment start so the next segment can be decoded
   * ahead. Waiting for a reserved gap also keeps element fallback from bypassing
   * a group boundary after Web Audio scheduling fails.
   */
  whenDrained() {
    if (!this.ctx) return Promise.resolve();
    const remainingMs = Math.max(0, (this.nextStartTime - this.ctx.currentTime) * 1000);
    if (remainingMs <= 0) return Promise.resolve();
    return new Promise((resolve) => {
      const entry = { resolve, timer: null };
      this._drainWaiters.add(entry);
      this._armDrainWaiter(entry);
    });
  }

  _armDrainWaiter(entry) {
    if (entry.timer !== null) clearTimeout(entry.timer);
    entry.timer = null;
    if (!this._drainWaiters.has(entry) || this.ctx?.state !== 'running') return;
    const remainingMs = Math.max(
      0,
      (this.nextStartTime - this.ctx.currentTime) * 1000,
    );
    if (remainingMs <= 0) {
      this._drainWaiters.delete(entry);
      entry.resolve();
      return;
    }
    entry.timer = setTimeout(() => {
      entry.timer = null;
      this._armDrainWaiter(entry);
    }, remainingMs);
  }

  _reconcileTimeline() {
    for (const source of [...this.activeSources]) {
      this._armSourceStart(source);
    }
    for (const entry of [...this._drainWaiters]) {
      this._armDrainWaiter(entry);
    }
  }

  /** Stop everything and release the AudioContext. */
  async close() {
    this.stop();
    if (this.ctx) {
      const ctx = this.ctx;
      ctx.removeEventListener?.('statechange', this._handleContextStateChange);
      try { await ctx.close(); } catch { /* ignore */ }
      if (this.ctx === ctx) {
        this.ctx = null;
        this.gain = null;
      }
    }
  }
}

export default GaplessScheduler;
